import { useState } from "react";
import { Share2, Twitter, Facebook, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Share buttons + dynamic OG card preview.
 * Uses backend /api/share/{id} URL for crawler-friendly OG meta tags
 * (top-level routes are shadowed by the SPA at the ingress).
 */
export default function ShareBar({ articleId, headline }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${BACKEND}/api/share/${articleId}`;
  const ogImage = `${BACKEND}/api/og/article/${articleId}.png`;
  const text = headline ? `${headline} — The Titan Standard` : "The Titan Standard";

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for browsers / iframes that block Clipboard API
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      console.error("ShareBar copy failed:", e);
      toast.error("Copy failed");
    }
  };

  const native = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url: shareUrl });
      } catch {
        // user cancelled — ignore
      }
    } else {
      copy();
    }
  };

  const twitter = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="titans-card p-5" data-testid="share-bar">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver flex items-center gap-2">
          <Share2 size={12} /> Share Card
        </div>
        <button
          onClick={native}
          data-testid="share-native-btn"
          className="font-cond uppercase tracking-wider text-[10px] text-titans-blue hover:text-white"
        >
          Quick Share
        </button>
      </div>

      <div className="relative mb-3 border border-titans-elev overflow-hidden">
        <img
          src={ogImage}
          alt="Share card preview"
          loading="lazy"
          data-testid="share-card-preview"
          className="w-full h-auto block"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <a
          href={twitter}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-twitter"
          className="bg-titans-navy hover:bg-titans-elev border border-titans-elev text-white font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5 transition-colors"
        >
          <Twitter size={12} /> X / Twitter
        </a>
        <a
          href={facebook}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-facebook"
          className="bg-titans-navy hover:bg-titans-elev border border-titans-elev text-white font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5 transition-colors"
        >
          <Facebook size={12} /> Facebook
        </a>
        <button
          onClick={copy}
          data-testid="share-copy"
          className={`font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5 transition-colors ${
            copied
              ? "bg-titans-blue text-white"
              : "bg-titans-red hover:bg-titans-red-hover text-white"
          }`}
        >
          {copied ? <><Check size={12} /> Copied</> : <><LinkIcon size={12} /> Copy Link</>}
        </button>
      </div>
      <div className="text-[10px] font-cond uppercase tracking-wider text-titans-muted mt-2">
        Drops a Titans-branded preview card on X, Facebook, iMessage & more.
      </div>
    </div>
  );
}
